/**
 * Import Units View
 * 
 * Handles importing units from a connected channel.
 * Now uses module's own hooks and services.
 */

import { FC, useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ToastMessageContext } from '@context';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useChannelUnitsForImportQuery, useImportUnitsMutation } from '../../hooks';
import { ChannelName, IChannelProperty } from '../../types';
import { CHANNEL_INFO, T_KEYS } from '../../constants';
import {
  SuccessStep,
  ImportPropertiesStep,
  ImportingModal,
  ImportUnitsHeader,
} from '../../components';
import { ConnectPageContainer } from '../../styles';

// Valid channel names
const VALID_CHANNELS: ChannelName[] = ['airbnb', 'booking', 'gathern'];

const ImportUnitsView: FC = () => {
  const navigate = useNavigate();
  const { integration } = useParams<{ integration: string }>();
  const [searchParams] = useSearchParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';
  const { showSuccess, showError } = useContext(ToastMessageContext);

  // Get channelId from query params
  const channelId = searchParams.get('channelId') || searchParams.get('channel_id');
  
  // Validate channel name
  const channelName = integration as ChannelName;
  const isValidChannel = VALID_CHANNELS.includes(channelName);
  
  // For Airbnb and Gathern, channelId is required
  const isChannelIdRequired = channelName === 'airbnb' || channelName === 'gathern';
  const hasRequiredChannelId = !isChannelIdRequired || !!channelId;
  
  // Validation error state
  const validationError = useMemo(() => {
    if (!isValidChannel) {
      return {
        titleKey: T_KEYS.INVALID_INTEGRATION,
        messageKey: T_KEYS.INVALID_INTEGRATION_DESC,
        messageParams: { integration },
      };
    }
    if (isChannelIdRequired && !channelId) {
      const channelDisplayName = channelName === 'gathern' ? 'Gathern' : 'Airbnb';
      return {
        titleKey: T_KEYS.MISSING_CHANNEL_ID,
        messageKey: T_KEYS.MISSING_CHANNEL_ID_DESC,
        messageParams: { channel: channelDisplayName },
      };
    }
    return null;
  }, [isValidChannel, isChannelIdRequired, channelId, integration, channelName, t]);

  // State
  const [currentStep, setCurrentStep] = useState<'success' | 'import'>('success');
  const [properties, setProperties] = useState<IChannelProperty[]>([]);
  const [importingIds, setImportingIds] = useState<string[]>([]);
  const [showImportingModal, setShowImportingModal] = useState(false);

  // Fetch units for import using module hook
  const { 
    data: unitsData, 
    isLoading: isUnitsLoading,
    error: unitsError,
    refetch: refetchUnits,
  } = useChannelUnitsForImportQuery(channelName, channelId);

  // Import mutation using module hook
  const { mutate: importUnits, isPending: isImporting } = useImportUnitsMutation();

  // Extract userChannelId and channelType from the query result
  const userChannelId = unitsData?.userChannelId ?? null;
  const apiChannelType = unitsData?.channelType ?? null;

  // Transform units data to IChannelProperty
  useEffect(() => {
    if (unitsData?.units) {
      setProperties(
        unitsData.units.map((unit) => ({
          id: unit.id,
          externalId: unit.propertyId,
          name: unit.name,
          location: unit.address,
          imageUrl: unit.imageUrl || undefined,
          isImported: unit.isSynced,
          channelName: channelName,
          syncStatus: unit.syncStatus,
          syncStatusName: unit.syncStatusName,
          syncStatusNameAr: unit.syncStatusNameAr,
        }))
      );
    }
  }, [unitsData, channelName]);

  // Handle import now
  const handleImportNow = useCallback(() => {
    setCurrentStep('import');
  }, []);

  // Handle import later
  const handleImportLater = useCallback(() => {
    navigate('/channels');
  }, [navigate]);

  // Handle start import
  const handleStartImport = useCallback((propertyIds: string[]) => {
    if (!userChannelId || apiChannelType === null) {
      showError(t(T_KEYS.CHANNEL_DATA_NOT_AVAILABLE));
      return;
    }

    setImportingIds(propertyIds);
    let apiCompleted = false;

    importUnits(
      { 
        userChannelId, 
        channelType: apiChannelType, 
        listings: unitsData?.units.filter(unit => propertyIds.includes(unit.id)).map(unit => ({listingId: unit.id, listingName: unit.name, imageUrl: unit.imageUrl ?? null})) || []
      },
      {
        onSuccess: (result) => {
          apiCompleted = true;
          setImportingIds([]);
          setProperties((prev) =>
            prev.map((p) =>
              propertyIds.includes(p.id) ? { ...p, isImported: true } : p
            )
          );
          const successMessage = isRtl ? result.messageAr : result.message;
          showSuccess(successMessage || t('CHANNELS.IMPORT_SUCCESS'));
        },
        onError: (error) => {
          apiCompleted = true;
          setImportingIds([]);
          const errorWithAr = error as Error & { messageAr?: string };
          const errorMessage = isRtl 
            ? (errorWithAr.messageAr || errorWithAr.message)
            : errorWithAr.message;
          showError(errorMessage || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );

    setTimeout(() => {
      if (!apiCompleted) {
        setShowImportingModal(true);
      }
    }, 2000);
  }, [userChannelId, apiChannelType, importUnits, unitsData, showSuccess, showError, t, isRtl]);

  // Handle go to dashboard
  const handleGoToDashboard = useCallback(() => {
    navigate('/properties-management');
  }, [navigate]);

  // Handle stay here
  const handleModalStayHere = useCallback(() => {
    setShowImportingModal(false);
    setImportingIds([]);
  }, []);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Get channel display name
  const channelDisplayName = useMemo(() => {
    if (!isValidChannel) return '';
    return isRtl ? CHANNEL_INFO[channelName].displayNameAr : CHANNEL_INFO[channelName].displayName;
  }, [isValidChannel, isRtl, channelName]);

  // Render error state for validation errors
  if (validationError) {
    return (
      <ConnectPageContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center',
            gap: 3,
            p: 4,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />
          <Typography variant="h5" fontWeight={600} color="error.main">
            {t(validationError.titleKey)}
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth={500}>
            {t(validationError.messageKey, validationError.messageParams)}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/channels')}
            sx={{ mt: 2 }}
          >
            {t(T_KEYS.BACK_TO_CHANNELS)}
          </Button>
        </Box>
      </ConnectPageContainer>
    );
  }

  // Render error state for API errors
  if (unitsError) {
    const errorWithAr = unitsError as Error & { errorMessageAr?: string };
    const errorMessage = isRtl 
      ? (errorWithAr.errorMessageAr || errorWithAr.message || 'حدث خطأ أثناء تحميل الوحدات. يرجى المحاولة مرة أخرى.')
      : (errorWithAr.message || 'An error occurred while loading units. Please try again.');
    
    return (
      <ConnectPageContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center',
            gap: 3,
            p: 4,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />
          <Typography variant="h5" fontWeight={600} color="error.main">
            {t(T_KEYS.ERROR_LOADING_UNITS)}
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth={500}>
            {errorMessage}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/channels')}>
              {t(T_KEYS.BACK_TO_CHANNELS)}
            </Button>
            <Button variant="contained" onClick={() => refetchUnits()}>
              {t(T_KEYS.TRY_AGAIN)}
            </Button>
          </Box>
        </Box>
      </ConnectPageContainer>
    );
  }

  return (
    <ConnectPageContainer>
      <ImportUnitsHeader
        channelName={channelName}
        channelDisplayName={channelDisplayName}
        onBack={handleBack}
      />

      {currentStep === 'success' && (
        <>
          <SuccessStep
            channelName={channelName}
            onImportNow={handleImportNow}
            onImportLater={handleImportLater}
          />
          <ImportPropertiesStep
            channelName={channelName}
            properties={properties}
            onStartImport={handleStartImport}
            onImportLater={handleImportLater}
            isLoading={isUnitsLoading}
            importingIds={importingIds}
            isImportButtonLoading={isImporting}
          />
        </>
      )}

      {currentStep === 'import' && (
        <ImportPropertiesStep
          channelName={channelName}
          properties={properties}
          onStartImport={handleStartImport}
          onImportLater={handleImportLater}
          isLoading={isUnitsLoading}
          importingIds={importingIds}
          isImportButtonLoading={isImporting}
        />
      )}

      <ImportingModal
        open={showImportingModal}
        onGoToDashboard={handleGoToDashboard}
        onStayHere={handleModalStayHere}
      />
    </ConnectPageContainer>
  );
};

export default ImportUnitsView;
